import BaseProduct from '@src/quince-site-common/src/domains/Product/BaseProduct'
import Bundle, { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { ProductOption, ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'

export interface getInitialSelectedOptionsArg {
  product: BaseProduct | Bundle
  selectedOptions?: ProductVariantOption[]
  fillAllOptions?: boolean
  ignoreVariantsVerification?: boolean
  defaultFillCount?: number
}

/*
For PDP:
1. No query params: Select first color
2. Color param but no size param: select color, size not selected on UI
3. No color param but size param: Select first color. respect size param.
4. Both size and color param: respect both params.
5. Invalid color param: select first color
6. Invalid size param: dont select size
*/
export function getInitialSelectedOptions({
  product,
  selectedOptions: selectedOptionsInput = [],
  fillAllOptions = false,
  ignoreVariantsVerification = false,
  defaultFillCount = 1,
}: getInitialSelectedOptionsArg) {
  const isBundle = product.type === PRODUCT_TYPES.BUNDLE
  const skipVariantVerification = !isBundle && product.id < 0 ? true : ignoreVariantsVerification
  const { productOptions } = product
  const selectedOptions = getSanitizeSelectedOptions(product, selectedOptionsInput, skipVariantVerification)
  const fillOptionsCount = fillAllOptions ? productOptions.length : defaultFillCount
  let toFillOptions = fillOptionsCount - selectedOptions.length
  const finalSelectedOptions = [...selectedOptions]
  const alreadySelectedOptionNames = new Set(selectedOptions.map(so => so.name))
  const sortedProductOptions = getSortedProductVariantOptionAsPerPriority(product, productOptions)

  for (const productOption of sortedProductOptions) {
    if (toFillOptions <= 0) {
      break
    }
    if (!alreadySelectedOptionNames.has(productOption.name)) {
      const possibleProductOptionValue = productOption.values.find(productOptionValue => {
        if (isBundle || skipVariantVerification) return true
        const variants = productHelper.getVariantsForOptions(product, [
          ...finalSelectedOptions,
          new ProductVariantOption({
            name: productOption.name,
            value: productOptionValue.value,
          }),
        ])
        return variants.length > 0
      })

      if (possibleProductOptionValue) {
        finalSelectedOptions.push(
          new ProductVariantOption({
            name: productOption.name,
            value: possibleProductOptionValue.value,
          }),
        )
      }
      toFillOptions -= 1
    }
  }

  // If a product Option is hidden or isCustomization, then always select first
  for (const productOption of productOptions) {
    const { isHidden, name, values } = productOption

    if (
      (isHidden || isCustomizationOption(productOption)) &&
      !alreadySelectedOptionNames.has(name) &&
      values.length > 0
    ) {
      finalSelectedOptions.push(
        new ProductVariantOption({
          name: name,
          value: values[0].value,
        }),
      )
      alreadySelectedOptionNames.add(name)
    }
  }
  return finalSelectedOptions
}

// Internal methods
function getSanitizeSelectedOptions(
  product: BaseProduct | Bundle,
  selectedOptions: ProductVariantOption[],
  ignoreVariantsVerification?: boolean,
): ProductVariantOption[] {
  const softSanitizedSelectedOptions = getSoftSanitizeSelectedOptions(product, selectedOptions)
  const sortedSanitizedSelectedOptions = getSortedProductVariantOptionAsPerPriority(
    product,
    softSanitizedSelectedOptions, // selectedOptions
  )
  if (ignoreVariantsVerification || product.type === PRODUCT_TYPES.BUNDLE) return sortedSanitizedSelectedOptions
  while (sortedSanitizedSelectedOptions.length !== 0) {
    const variants = productHelper.getVariantsForOptions(product, sortedSanitizedSelectedOptions)
    if (variants.length) return sortedSanitizedSelectedOptions
    const removedOption = sortedSanitizedSelectedOptions.pop()
    if (removedOption && isCustomizationOption(removedOption) && sortedSanitizedSelectedOptions.length) {
      sortedSanitizedSelectedOptions.unshift(removedOption)
      sortedSanitizedSelectedOptions.pop()
    }
  }
  return sortedSanitizedSelectedOptions
}

function getSoftSanitizeSelectedOptions(
  product: BaseProduct | Bundle,
  selectedOptions: ProductVariantOption[],
): ProductVariantOption[] {
  const productionOptionNameAndValueSetRecord = getProductionOptionNameAndValueSetRecord(product.productOptions)
  return selectedOptions.filter(productOption => {
    return (
      isCustomizationOption(productOption) ||
      productionOptionNameAndValueSetRecord[productOption.name]?.has(productOption.value)
    )
  })
}

function getProductionOptionNameAndValueSetRecord(
  productOptions: ProductOption[],
): Record<string, Set<string> | undefined> {
  return productOptions.reduce<Record<string, Set<string>>>((productOptionNameByValueSet, productOption) => {
    const valueSet = productOptionNameByValueSet[productOption.name] || new Set<string>()
    productOption.values.forEach(optionValue => {
      valueSet.add(optionValue.value)
    })
    productOptionNameByValueSet[productOption.name] = valueSet
    return productOptionNameByValueSet
  }, {})
}

function getSortedProductVariantOptionAsPerPriority<T extends ProductVariantOption | ProductOption>(
  product: BaseProduct | Bundle,
  options: T[],
) {
  const productOptionNameAndPriorityRecord = product.productOptions.reduce<Record<string, number>>(
    (productOptionNameAndPriorityRecord, productOption) => {
      productOptionNameAndPriorityRecord[productOption.name] = productOption.displaySequence
      return productOptionNameAndPriorityRecord
    },
    {},
  )
  return [...options].sort((option1, option2) => {
    const priorityForOption1 = productOptionNameAndPriorityRecord[option1.name]
    const priorityForOption2 = productOptionNameAndPriorityRecord[option2.name]
    return priorityForOption1 - priorityForOption2
  })
}

function isCustomizationOption(option: ProductVariantOption | ProductOption) {
  return option.name === ProductOptionName.Customization || option.name === ProductOptionName.Engraving
}
